// warrantyAttachments — shared upload plumbing for warranty-claim photos + files.
//
// Why this exists (Sep 15, 2026): the three warranty upload paths (deal
// Warranty tab, Open-claim modal, homeowner form) each carried their own copy
// of the upload loop, and all three swallowed failures with console.warn — a
// rejected upload looked exactly like a successful one. Two concrete traps:
//
//   • The deal-documents / homeowner-uploads buckets enforce a MIME allowlist,
//     and the loops fell back to "application/octet-stream" whenever the
//     browser left file.type blank (HEIC on Android + Windows, anything picked
//     through a generic file picker). The bucket rejected it and the file
//     silently vanished.
//   • Only images were accepted at all — no PDFs, no Word/Excel documents.
//
// This module owns: MIME inference by extension, the allowlist (it MIRRORS
// storage.buckets.allowed_mime_types for both buckets — change them together),
// image-vs-document classification, and upload loops that RETURN per-file
// failures instead of hiding them.

import { supabase, supabaseAnon } from "./supabase";
import { heicToJpeg } from "./heicToJpeg";
import { fmtSupabaseError } from "./fmtError";

export const WARRANTY_FILE_TYPES = {
  photo: "warranty_photo",
  document: "warranty_document",
} as const;

/** Every deal_documents.file_type that belongs to a warranty claim. */
export const WARRANTY_ATTACHMENT_FILE_TYPES: string[] = [
  WARRANTY_FILE_TYPES.photo,
  WARRANTY_FILE_TYPES.document,
];

/** Extension → MIME. Mirrors the bucket allowlists (migration 20260915_warranty_attachments). */
const EXT_MIME: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  heic: "image/heic",
  heif: "image/heif",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  txt: "text/plain",
  csv: "text/csv",
};
const ALLOWED_MIME = new Set(Object.values(EXT_MIME));
const MIME_EXT: Record<string, string> = Object.fromEntries(
  Object.entries(EXT_MIME).map(([ext, mime]) => [mime, ext]),
);
// Prefer the canonical extension when two map to one MIME (jpeg → jpg).
MIME_EXT["image/jpeg"] = "jpg";

/** `accept` values for the three picker inputs. */
export const ACCEPT_IMAGES = "image/*";
export const ACCEPT_ANY_ATTACHMENT = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv";
export const SUPPORTED_TYPES_LABEL = "photos (JPG, PNG, HEIC, WEBP, GIF), PDFs, Word, Excel, or text files";
/** Matches storage.buckets.file_size_limit on both buckets. */
export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

export function isImageMime(mime: string | null | undefined): boolean {
  return !!mime && mime.toLowerCase().startsWith("image/");
}

/** deal_documents.file_type for a given MIME: images are photos, everything else a document. */
export function warrantyFileTypeFor(mime: string | null | undefined): string {
  return isImageMime(mime) ? WARRANTY_FILE_TYPES.photo : WARRANTY_FILE_TYPES.document;
}

/**
 * Resolve a MIME type the buckets will accept. The browser's declared type
 * wins when it is on the allowlist; otherwise fall back to the extension.
 * Returns null when neither yields a supported type — callers must reject the
 * file with a clear message rather than uploading as octet-stream.
 */
export function resolveAttachmentMime(file: File): string | null {
  const declared = (file.type || "").toLowerCase().trim();
  if (ALLOWED_MIME.has(declared)) return declared;
  const ext = (file.name.toLowerCase().split(".").pop() || "").trim();
  const byExt = EXT_MIME[ext];
  return byExt ?? null;
}

export function fmtBytes(n: number | null | undefined): string {
  if (!n || n <= 0) return "";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

export interface PreparedAttachment {
  file: File;
  mime: string;
  fileType: string;
  ext: string;
}

export interface AttachmentFailure {
  name: string;
  reason: string;
}

type PrepareResult =
  | { ok: true; prepared: PreparedAttachment }
  | { ok: false; failure: AttachmentFailure };

/**
 * Normalize one picked file: HEIC → JPEG, MIME resolution, size + type checks.
 * Never throws — an unusable file comes back as a failure with a human reason.
 */
export async function prepareAttachment(picked: File): Promise<PrepareResult> {
  let file = picked;
  try {
    file = await heicToJpeg(picked);
  } catch {
    file = picked;
  }
  if (file.size === 0) {
    return { ok: false, failure: { name: picked.name, reason: "the file is empty" } };
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return { ok: false, failure: { name: picked.name, reason: `larger than ${fmtBytes(MAX_ATTACHMENT_BYTES)}` } };
  }
  const mime = resolveAttachmentMime(file);
  if (!mime) {
    return {
      ok: false,
      failure: { name: picked.name, reason: `unsupported type — use ${SUPPORTED_TYPES_LABEL}` },
    };
  }
  const nameExt = (file.name.toLowerCase().split(".").pop() || "").trim();
  const ext = EXT_MIME[nameExt] === mime ? nameExt : (MIME_EXT[mime] ?? "bin");
  return { ok: true, prepared: { file, mime, fileType: warrantyFileTypeFor(mime), ext } };
}

function randomSuffix(): string {
  return Math.random().toString(36).slice(2, 8);
}

function safeFileName(name: string): string {
  return name.replace(/[^A-Za-z0-9._-]/g, "_").slice(-120) || "file";
}

export interface UploadedAttachment {
  storage_path: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  file_type: string;
}

export interface UploadResult {
  uploaded: UploadedAttachment[];
  failures: AttachmentFailure[];
  photoCount: number;
  documentCount: number;
}

export interface StaffUploadArgs {
  dealId: string;
  claimId: string;
  files: File[];
  /** team_roster.id of the uploader; the photo_added timeline event needs it. */
  uploaderId: string | null | undefined;
  uploaderLabel?: string | null;
  note?: string | null;
  onProgress?: (done: number, total: number) => void;
}

/**
 * Staff path: upload into deal-documents/warranty/{deal}/{claim}/, insert the
 * deal_documents row (linked to the claim), and log a photo_added timeline
 * event. Each file is independent — one bad file never blocks the others, and
 * every failure comes back with its reason so the UI can show it.
 */
export async function uploadStaffWarrantyAttachments(args: StaffUploadArgs): Promise<UploadResult> {
  const { dealId, claimId, files, uploaderId, uploaderLabel, note, onProgress } = args;
  const result: UploadResult = { uploaded: [], failures: [], photoCount: 0, documentCount: 0 };
  let done = 0;
  for (const picked of files) {
    const prep = await prepareAttachment(picked);
    if (!prep.ok) {
      result.failures.push(prep.failure);
      onProgress?.(++done, files.length);
      continue;
    }
    const { file, mime, fileType, ext } = prep.prepared;
    const path = `warranty/${dealId}/${claimId}/${Date.now()}-${randomSuffix()}.${ext}`;

    const { error: uploadErr } = await supabase.storage
      .from("deal-documents")
      .upload(path, file, { contentType: mime, upsert: false });
    if (uploadErr) {
      result.failures.push({ name: picked.name, reason: fmtSupabaseError(uploadErr) });
      onProgress?.(++done, files.length);
      continue;
    }

    const { error: docErr } = await supabase.from("deal_documents").insert({
      deal_id: dealId,
      file_name: file.name,
      file_type: fileType,
      storage_path: path,
      file_size: file.size,
      mime_type: mime,
      uploaded_by: uploaderId ?? null,
      warranty_claim_id: claimId,
      notes: note ?? null,
    });
    if (docErr) {
      // Don't strand an orphan object in the bucket — best effort.
      await supabase.storage.from("deal-documents").remove([path]).then(() => undefined, () => undefined);
      result.failures.push({ name: picked.name, reason: fmtSupabaseError(docErr) });
      onProgress?.(++done, files.length);
      continue;
    }

    // Timeline event — best effort; the document row is the record that matters.
    if (uploaderId) {
      const { error: evErr } = await supabase.from("warranty_claim_events").insert({
        claim_id: claimId,
        kind: "photo_added",
        actor_id: uploaderId,
        actor_label: uploaderLabel || "Staff",
        payload: { storage_path: path, file_name: file.name, mime_type: mime, file_type: fileType },
      });
      if (evErr) console.warn("[warrantyAttachments] timeline event failed:", evErr.message);
    }

    result.uploaded.push({ storage_path: path, file_name: file.name, mime_type: mime, file_size: file.size, file_type: fileType });
    if (fileType === WARRANTY_FILE_TYPES.photo) result.photoCount += 1;
    else result.documentCount += 1;
    onProgress?.(++done, files.length);
  }
  return result;
}

export interface HomeownerUploadArgs {
  dealId: string;
  claimId: string;
  files: File[];
  onProgress?: (done: number, total: number) => void;
}

/**
 * Homeowner path: anon upload into homeowner-uploads/{deal}/warranty/{claim}/.
 * Registration of the deal_documents rows happens server-side via the
 * homeowner-warranty-claim edge function ("add_photo") — the caller passes
 * `result.uploaded` straight through as its `photos` payload.
 */
export async function uploadHomeownerWarrantyAttachments(args: HomeownerUploadArgs): Promise<UploadResult> {
  const { dealId, claimId, files, onProgress } = args;
  const result: UploadResult = { uploaded: [], failures: [], photoCount: 0, documentCount: 0 };
  let done = 0;
  for (const picked of files) {
    const prep = await prepareAttachment(picked);
    if (!prep.ok) {
      result.failures.push(prep.failure);
      onProgress?.(++done, files.length);
      continue;
    }
    const { file, mime, fileType } = prep.prepared;
    const path = `${dealId}/warranty/${claimId}/${Date.now()}-${randomSuffix()}-${safeFileName(file.name)}`;
    const { error: uploadErr } = await supabaseAnon.storage
      .from("homeowner-uploads")
      .upload(path, file, { contentType: mime, upsert: false });
    if (uploadErr) {
      result.failures.push({ name: picked.name, reason: fmtSupabaseError(uploadErr) });
      onProgress?.(++done, files.length);
      continue;
    }
    result.uploaded.push({ storage_path: path, file_name: file.name, mime_type: mime, file_size: file.size, file_type: fileType });
    if (fileType === WARRANTY_FILE_TYPES.photo) result.photoCount += 1;
    else result.documentCount += 1;
    onProgress?.(++done, files.length);
  }
  return result;
}

/** "3 photos and 1 file" — for toasts + notifications. */
export function describeAttachmentCounts(photoCount: number, documentCount: number): string {
  const parts: string[] = [];
  if (photoCount > 0) parts.push(`${photoCount} photo${photoCount === 1 ? "" : "s"}`);
  if (documentCount > 0) parts.push(`${documentCount} file${documentCount === 1 ? "" : "s"}`);
  return parts.join(" and ") || "0 files";
}

/** Summarize failures for an inline error line. */
export function describeFailures(failures: AttachmentFailure[]): string {
  return failures.map((f) => `${f.name} — ${f.reason}`).join("; ");
}
